/**
 * Platform staff (ClanFire's own team), separate from per-server roles.
 *
 *  - CEO:       everything, including appointing Admins and Moderators.
 *  - ADMIN:     suspend/unsuspend members and Moderators, see emails.
 *  - MODERATOR: suspend/unsuspend regular members.
 *  - SYSTEM:    the built-in "ClanFire" account; can't be acted on.
 *
 * The CEO rank itself is only granted from the server console
 * (`npm run staff -- ceo <username>`), never through the API.
 */
export type StaffRole = 'CEO' | 'ADMIN' | 'MODERATOR' | 'SYSTEM';

/** Ranks that can use the staff panel. */
export type StaffRank = Exclude<StaffRole, 'SYSTEM'>;

export const STAFF_LEVEL: Record<StaffRank, number> = { CEO: 3, ADMIN: 2, MODERATOR: 1 };

export const STAFF_LABEL: Record<StaffRole, string> = {
  CEO: 'CEO',
  ADMIN: 'Admin',
  MODERATOR: 'Moderator',
  SYSTEM: 'Official',
};

export function isStaff(role: StaffRole | null | undefined): role is StaffRank {
  return role === 'CEO' || role === 'ADMIN' || role === 'MODERATOR';
}

/** Whether staff member `actor` may suspend/unsuspend `target` (strictly lower rank, never SYSTEM). */
export function staffOutranks(actor: StaffRole | null | undefined, target: StaffRole | null | undefined): boolean {
  if (!isStaff(actor) || target === 'SYSTEM') return false;
  const targetLevel = isStaff(target) ? STAFF_LEVEL[target] : 0;
  return STAFF_LEVEL[actor] > targetLevel;
}

/* ------------------------------------------------------------------ */
/* Reports                                                             */
/* ------------------------------------------------------------------ */

export const REPORT_REASONS = [
  { id: 'SPAM', label: 'Spam or scam' },
  { id: 'HARASSMENT', label: 'Harassment or bullying' },
  { id: 'HATE', label: 'Hate speech' },
  { id: 'VIOLENCE', label: 'Threats or violence' },
  { id: 'SEXUAL', label: 'Unwanted sexual content' },
  { id: 'CHILD_SAFETY', label: 'Child safety' },
  { id: 'SELF_HARM', label: 'Self-harm or suicide' },
  { id: 'ILLEGAL', label: 'Something else illegal' },
  { id: 'OTHER', label: 'Something else' },
] as const;

export type ReportReason = (typeof REPORT_REASONS)[number]['id'];
export type ReportStatus = 'OPEN' | 'RESOLVED' | 'DISMISSED';

export const REPORT_REASON_LABEL = Object.fromEntries(REPORT_REASONS.map((r) => [r.id, r.label])) as Record<ReportReason, string>;
